import React from 'react';
import { Helmet } from 'react-helmet-async';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useTranslation } from '@shared/core/i18n';
import type { FaqItem } from '@apps/card/routes/PublicShop/components/FaqSection/useStationeryFaqItems';

type StationerySchemaProps = Readonly<{
  category: StationeryTemplateCategoryEnum;
  title: string;
  description: string;
  canonicalURL: string;
  faqItems: ReadonlyArray<FaqItem>;
}>;

const useBuildBreadcrumbList = (category: StationeryTemplateCategoryEnum, canonicalURL: string) => {
  const { tq } = useTranslation('stationery');

  const categoryDisplayName = {
    [StationeryTemplateCategoryEnum.saveTheDate]: tq`Save the Date`,
    [StationeryTemplateCategoryEnum.invitation]: tq`Invitation`,
    [StationeryTemplateCategoryEnum.thankYou]: tq`Thank You`,
    [StationeryTemplateCategoryEnum.holiday]: tq`Holiday`,
    [StationeryTemplateCategoryEnum.enclosure]: tq`Enclosure`,
    [StationeryTemplateCategoryEnum.menu]: tq`Menu`,
    [StationeryTemplateCategoryEnum.napkin]: tq`Napkin`,
    [StationeryTemplateCategoryEnum.sign]: tq`Sign`,
    [StationeryTemplateCategoryEnum.monogram]: tq`Monogram`,
    // Required for exhaustiveness only — no envelope category page exists to emit breadcrumbs for.
    [StationeryTemplateCategoryEnum.envelope]: tq`Envelope`,
  }[category];

  return {
    '@type': 'BreadcrumbList',
    '@id': `${canonicalURL}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: tq`Home`,
        item: 'https://withjoy.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: tq`Cards`,
        item: 'https://withjoy.com/cards/',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: tq`Wedding`,
        item: 'https://withjoy.com/cards/wedding/',
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: categoryDisplayName,
        item: canonicalURL,
      },
    ],
  };
};

const useBuildFaqPage = (faqItems: ReadonlyArray<FaqItem>, canonicalURL: string) => {
  if (faqItems.length === 0) return null;

  return {
    '@type': 'FAQPage',
    '@id': `${canonicalURL}#faq`,
    mainEntity: faqItems.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
};

// Mirrors the sibling /wedding-website/ SoftwareApplication schema.
const SOFTWARE_APPLICATION_SCHEMA = {
  '@type': 'SoftwareApplication',
  '@id': 'https://withjoy.com/#software-application',
  name: 'Joy – Wedding Planner & Website',
  applicationCategory: 'LifestyleApplication',
  operatingSystem: 'iOS, Android',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
  sameAs: ['https://apps.apple.com/us/app/joy-wedding-planner-website/id994411720', 'https://play.google.com/store/apps/details?id=com.withjoy.joy'],
};

export const StationerySchema: React.FC<StationerySchemaProps> = ({ category, title, description, canonicalURL, faqItems }) => {
  const breadcrumbList = useBuildBreadcrumbList(category, canonicalURL);
  const faqPage = useBuildFaqPage(faqItems, canonicalURL);

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Organization',
              '@id': 'https://withjoy.com/#organization',
              name: 'Joy',
              url: 'https://withjoy.com/',
              // same as the one used by marcom
              logo: 'https://withjoy.com/assets/public/marcom-prod/legacy/favicon/android-icon-192x192.png?opt=aggressive&ver=2',
              sameAs: ['https://www.instagram.com/withjoy/', 'https://www.pinterest.com/withjoy/', 'https://www.facebook.com/withjoy/'],
            },
            {
              '@type': 'WebSite',
              '@id': 'https://withjoy.com/#website',
              url: 'https://withjoy.com/',
              name: 'Joy',
              publisher: { '@id': 'https://withjoy.com/#organization' },
            },
            {
              '@type': 'WebPage',
              '@id': `${canonicalURL}#webpage`,
              url: canonicalURL,
              name: title,
              description,
              isPartOf: { '@id': 'https://withjoy.com/#website' },
              breadcrumb: { '@id': `${canonicalURL}#breadcrumb` },
            },
            ...(breadcrumbList ? [breadcrumbList] : []),
            ...(faqPage ? [faqPage] : []),
            SOFTWARE_APPLICATION_SCHEMA,
          ],
        })}
      </script>
    </Helmet>
  );
};
